<template>
  <div class="right-sidebar">
    <div class="config-header">
      <div class="header-main">
        <div class="header-left">
          <div class="header-icon">
            <SvgAsset :content="sidebarConfigBookSvg" />
          </div>
          <div class="header-copy">
            <span class="header-title">{{ t('base_set_config') }}</span>
          </div>
          <!-- 推荐配置按钮紧贴标题，与窗口控制区语义隔离 -->
          <button class="recommend-header-btn" v-if="configSet.recommendConfigList.value.length > 0 && !isJiangguowangSupported" :title="t('config_recommend')" @click="recommendVisible = true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            <span>{{ t('config_recommend') }}</span>
            <span class="recommend-header-dot"></span>
          </button>
        </div>

        <div class="header-main-right">
          <div class="window-controls" v-if="isElectron()">
            <button class="window-btn" :title="t('minimize') || 'Minimize'" @click="min">
              <SvgAsset :content="windowMinimizeSvg" />
            </button>
            <button class="window-btn" :title="windowControlTitle" @click="max">
              <SvgAsset v-if="!isWindowMaximized" :content="windowMaximizeSvg" />
              <SvgAsset v-else :content="windowRestoreSvg" />
            </button>
            <button class="window-btn close-btn" :title="t('close') || 'Close'" @click="out">
              <SvgAsset :content="windowCloseSvg" />
            </button>
          </div>
        </div>
      </div>

      <div class="header-actions" v-if="isSupportExport">
        <div class="header-actions-label"></div>
        <div class="action-item action-item-wide">
          <import_config ref="importConfigRef" :title="t('import_config')" />
          <div class="tip_bubble">{{ t('import_config') }}</div>
        </div>
        <div class="action-item action-item-wide">
          <export_config :title="t('export_config')" />
          <div class="tip_bubble">{{ t('export_config') }}</div>
        </div>
      </div>
    </div>

    <!-- 键盘预设 首次引导卡片 — 不再需要，4个配置自动创建 -->

    <div class="config-list scrollbar_thumb" ref="configListRef">
      <!-- ─── 活跃配置区域（4 个固定槽位） ─── -->
      <template v-if="configSet.isOnboardConfigSupported && configSet.activeConfigs.length > 0">
        <div class="config-section-micro-label">
          <span>{{ t('onboard_config_section_label') }}</span>
          <div class="section-hint-wrap" @mouseenter="showSectionHint($event, 'onboard')" @mouseleave="hideSectionHint">
            <div class="section-hint-icon">?</div>
          </div>
          <span class="micro-count">{{ configSet.activeConfigs.length }} / 4</span>
        </div>

        <transition-group name="list-complete">
          <config_list_item
            v-for="activeItem in configSet.activeConfigs"
            :key="'active-' + activeItem.Name + activeItem.index"
            :item="activeItem"
            :index="activeItem.index"
            :is-active="activeItem.index == configSet.currentConfig.value"
            variant="active"
            :is-feature-supported="configSet.isFeatureSupported"
            :onboard-slot="activeItem.OnboardSlot"
            @click="change"
            @rename-complete="onItemRenameComplete"
            @set-shortcut="setBind"
            @go-device-function="goToDeviceFunction"
          />
        </transition-group>
      </template>

      <!-- ─── 不支持板载的设备：显示原始完整列表 ─── -->
      <template v-if="!configSet.isOnboardConfigSupported">
        <transition-group name="list-complete">
          <config_list_item
            v-for="(item, index) in configSet.configList.value"
            :key="item.Name + index"
            :item="item"
            :index="index"
            :is-active="index == configSet.currentConfig.value"
            variant="normal"
            :is-feature-supported="configSet.isFeatureSupported"
            @click="change"
            @rename-complete="onItemRenameComplete"
            @delete="deleteConfig"
            @set-shortcut="setBind"
          />
        </transition-group>
      </template>

      <!-- ─── 存档配置区域（未绑定板载的配置） ─── -->
      <template v-if="configSet.isOnboardConfigSupported && (configSet.archivedConfigs.length > 0 || configSet.configList.value.length < 8)">
        <div class="config-section-micro-label config-section-micro-archived">
          <span>{{ t('other_config') }}</span>
          <div class="section-hint-wrap" @mouseenter="showSectionHint($event, 'local')" @mouseleave="hideSectionHint">
            <div class="section-hint-icon">?</div>
          </div>
          <span class="micro-count" v-if="configSet.archivedConfigs.length > 0">{{ configSet.archivedConfigs.length }}</span>
        </div>

        <transition-group name="list-complete">
          <config_list_item
            v-for="archiveItem in configSet.archivedConfigs"
            :key="'archive-' + archiveItem.Name + archiveItem.index"
            :item="archiveItem"
            :index="archiveItem.index"
            :is-active="false"
            variant="archive"
            :is-feature-supported="false"
            @click="changeOrReplace"
            @rename-complete="onItemRenameComplete"
            @delete="deleteConfig"
          />
        </transition-group>

        <!-- 板载设备：在存档区内新建存档配置（总数 < 8） -->
        <div class="add-config" @click="addConfig" v-if="configSet.isOnboardConfigSupported && configSet.configList.value.length < 8">
          <div class="plus-icon">
            <SvgAsset :content="actionPlusSvg" />
          </div>
          <span>{{ t('config_create_title') }}</span>
        </div>
      </template>

      <div class="add-config" @click="addConfig" v-if="!configSet.isOnboardConfigSupported && configSet.configList.value.length < 6">
        <div class="plus-icon">
          <SvgAsset :content="actionPlusSvg" />
        </div>
        <span>{{ t('config_create_title') }}</span>
      </div>
    </div>

    <div class="config-footer-fixed" v-if="!configSet.isOnboardConfigSupported && configSet.configList.value.length >= 6">
      <div class="add-config" @click="addConfig">
        <div class="plus-icon">
          <SvgAsset :content="actionPlusSvg" />
        </div>
        <span>{{ t('config_create_title') }}</span>
      </div>
    </div>

    <!-- 讲过往 推荐配置卡片 -->
    <jiangguowang_recommend_card v-if="isJiangguowangCardVisible" @need-import-replace="onJiangguowangNeedImportReplace" @need-replace-slot="onJiangguowangNeedReplaceSlot" @scroll-to-bottom="onJiangguowangScrollToBottom" @close="onJiangguowangClose" />

    <!-- 讲过往卡片已关闭时的重新展开入口 -->
    <div v-if="isJiangguowangCollapsed" class="jiangguowang-reopen-bar" @click="onJiangguowangReopen">
      <span class="reopen-dot"></span>
      <span class="reopen-label">{{ t('jiangguowang_recommend') }}</span>
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
        <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </div>

    <shortcon_key v-if="configSet.isShowShortcon == true" :keys="bindKeys" :index="bindIndex" />
    <config_migration_dialog />

    <!-- 推荐配置弹窗 -->
    <recommend_config_dialog :visible="recommendVisible" @close="recommendVisible = false" @applied="onRecommendApplied" />

    <!-- 替换槽位对话框 -->
    <replace_slot_dialog :visible="replaceSlotVisible" @close="replaceSlotVisible = false" @confirm="onReplaceConfirm" />

    <!-- ─── Section Hint 气泡（Teleport 到 body，避免被 overflow 裁剪） ─── -->
    <Teleport to="body">
      <Transition name="hint-fade">
        <div v-if="sectionHintVisible" class="section-hint-teleport-bubble" :style="sectionHintStyle">
          <div class="hint-bubble-title">{{ sectionHintTitle }}</div>
          <div class="hint-bubble-body">{{ sectionHintText }}</div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue'
import { configSet, configModel } from '@/service/driver/config_set'
import { operate, OperateType } from '@/service/driver/operate'
import { actionSet } from '@/service/driver/action_set'
import { ActionType, SetType } from '@/service/driver/action_types'
import import_config from '../operation_config/import_config.vue'
import export_config from '../operation_config/export_config.vue'
import recommend_config_dialog from './recommend_config_dialog.vue'
import replace_slot_dialog from './replace_slot_dialog.vue'
import config_list_item from './config_list_item.vue'
import jiangguowang_recommend_card from './jiangguowang_recommend_card.vue'
import { DeviceManager, DeviceProtocol } from '@/service/device'
import { DialogType, enableDialog } from '../cdialog/dialog'
import i18n from '@/languages/i18n'
import { VK, DevicePid } from '@/define/define'
import shortcon_key from '../operation_config/shortcon_key.vue'
import config_migration_dialog from './config_migration_dialog.vue'
import { isElectron } from '@libs/utils/envUtils'
import SvgAsset from '../common/SvgAsset.vue'
import actionPlusSvg from '@/assets/inline-svg/action-plus.svg?raw'
import sidebarConfigBookSvg from '@/assets/inline-svg/sidebar-config-book.svg?raw'
import windowCloseSvg from '@/assets/inline-svg/window-close.svg?raw'
import windowMaximizeSvg from '@/assets/inline-svg/window-maximize.svg?raw'
import windowMinimizeSvg from '@/assets/inline-svg/window-minimize.svg?raw'
import windowRestoreSvg from '@/assets/inline-svg/window-restore.svg?raw'
const { t } = i18n.global

// ─── Section Hint Teleport 状态 ───
const sectionHintVisible = ref(false)
const sectionHintText = ref('')
const sectionHintTitle = ref('')
const sectionHintStyle = ref<Record<string, string>>({})
let sectionHintTimer: ReturnType<typeof setTimeout> | null = null

const showSectionHint = (e: MouseEvent, type: 'onboard' | 'local') => {
  if (sectionHintTimer) clearTimeout(sectionHintTimer)
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  const bubbleWidth = 220
  let left = rect.left + rect.width / 2 - bubbleWidth / 2
  // 不超出视口右侧
  if (left + bubbleWidth > window.innerWidth - 12) left = window.innerWidth - bubbleWidth - 12
  // 不超出视口左侧
  if (left < 8) left = 8
  sectionHintStyle.value = {
    top: `${rect.bottom + 10}px`,
    left: `${left}px`,
    width: `${bubbleWidth}px`,
  }
  sectionHintTitle.value = type === 'onboard' ? t('onboard_config_section_label') : t('other_config')
  sectionHintText.value = type === 'onboard' ? t('onboard_config_section_hint') : t('local_config_section_hint')
  sectionHintVisible.value = true
}

const hideSectionHint = () => {
  sectionHintTimer = setTimeout(() => {
    sectionHintVisible.value = false
  }, 120)
}

const configListRef = ref<HTMLElement | null>(null)
const importConfigRef = ref<{ openReplaceDialog: (name: string, content: string) => void } | null>(null)
const bindIndex = ref(0)
const bindKeys = ref<VK[]>([])
const isWindowMaximized = ref(false)

// ─── 替换槽位对话框状态 ───
const replaceSlotVisible = ref(false)
const replaceSlotTargetIndex = ref(-1)

// ─── 推荐配置弹窗状态 ───
const recommendVisible = ref(false)

const onRecommendApplied = () => {
  nextTick(() => {
    if (configListRef.value) {
      configListRef.value.scrollTo({ top: configListRef.value.scrollHeight, behavior: 'smooth' })
    }
  })
}

const onReplaceConfirm = async (slotIndex: number) => {
  replaceSlotVisible.value = false
  await configSet.apiReplaceSlot(replaceSlotTargetIndex.value, slotIndex)
}

const changeOrReplace = (index: number) => {
  if (configSet.isOnboardConfigSupported) {
    const item = configSet.configList.value[index]
    if (item?.OnboardSlot === null || item?.OnboardSlot === undefined) {
      replaceSlotTargetIndex.value = index
      replaceSlotVisible.value = true
      return
    }
  }
  change(index)
}

const syncWindowMaximizedState = async () => {
  if (!isElectron()) {
    isWindowMaximized.value = false
    return
  }
  isWindowMaximized.value = Boolean(await electronAPI.wnd('isMaximized'))
}

const handleWindowResize = () => {
  void syncWindowMaximizedState()
}

const windowControlTitle = computed(() => (isWindowMaximized.value ? t('restore') || 'Restore' : t('maximize') || 'Maximize'))

onMounted(() => {
  void syncWindowMaximizedState()
  if (isElectron()) {
    window.addEventListener('resize', handleWindowResize)
  }
  setTimeout(() => {
    nextTick(() => {
      if (configListRef.value) {
        configListRef.value.scrollTo({
          top: configListRef.value.scrollHeight,
          behavior: 'smooth',
        })
      }
    })
  }, 300)
})

onUnmounted(() => {
  if (isElectron()) {
    window.removeEventListener('resize', handleWindowResize)
  }
})

const JIANGGUOWANG_DISMISSED_KEY = 'jiangguowang_card_dismissed'
const jiangguowangDismissed = ref(localStorage.getItem(JIANGGUOWANG_DISMISSED_KEY) === 'true')

const isJiangguowangSupported = computed(() => {
  const currentPid = DeviceManager.instance.state.currentPid
  return currentPid === DevicePid.IROKRA68 || currentPid === DevicePid.IROKRA68_1 || currentPid === DevicePid.IROKMG75V2C12
})

const isJiangguowangCardVisible = computed(() => isJiangguowangSupported.value && !jiangguowangDismissed.value)
const isJiangguowangCollapsed = computed(() => isJiangguowangSupported.value && jiangguowangDismissed.value)

// ─── 讲过往卡片事件处理 ───
const onJiangguowangClose = () => {
  jiangguowangDismissed.value = true
  localStorage.setItem(JIANGGUOWANG_DISMISSED_KEY, 'true')
}

const onJiangguowangReopen = () => {
  jiangguowangDismissed.value = false
  localStorage.removeItem(JIANGGUOWANG_DISMISSED_KEY)
}

const onJiangguowangNeedImportReplace = (title: string, content: string) => {
  importConfigRef.value?.openReplaceDialog(title, content)
}

const onJiangguowangNeedReplaceSlot = (index: number) => {
  replaceSlotTargetIndex.value = index
  replaceSlotVisible.value = true
}

const onJiangguowangScrollToBottom = () => {
  nextTick(() => {
    if (configListRef.value) {
      configListRef.value.scrollTo({ top: configListRef.value.scrollHeight, behavior: 'smooth' })
    }
  })
}

const isSupportExport = computed(() => {
  return DeviceManager.instance.state.driverProtocol == DeviceProtocol.hid_web
})

const setBind = (index: number) => {
  configSet.isShowShortcon = true
  bindIndex.value = index
  bindKeys.value = configSet.configList.value[index].Shortcut || []
}

const change = (index: number) => {
  if (configSet.currentConfig.value === index) return
  configSet.apiChangeConfig(index)
}

// ─── 配置项重命名回调（由 config_list_item 组件 rename-complete 事件触发） ───
const onItemRenameComplete = async (index: number, newName: string) => {
  await configSet.apiReName(index, newName)
  enableDialog(t('rename_success'), DialogType.notice, true)
}

const deleteConfig = async (index: number) => {
  // 不支持板载的设备：禁止删除当前选中的配置
  // 支持板载的设备：禁止删除活跃配置（有槽位的）
  if (configSet.isOnboardConfigSupported) {
    const item = configSet.configList.value[index]
    if (item?.OnboardSlot !== null && item?.OnboardSlot !== undefined) {
      enableDialog(t('config_set_delete_message'), DialogType.notice, true)
      return
    }
  } else {
    if (index == configSet.currentConfig.value) {
      enableDialog(t('config_set_delete_message'), DialogType.notice, true)
      return
    }
  }

  const confirm = await enableDialog(t('delete_config_message'), DialogType.confirm)
  if (confirm) {
    try {
      await configSet.apiDelete(index)
      enableDialog(t('delete_config_success'), DialogType.notice, true)
    } catch (e) {
      configSet.apiConfigList()
      enableDialog(t('delete_config_fail'), DialogType.notice, false)
    }
  }
}

const addConfig = async () => {
  await configSet.apiAddConfig()
  nextTick(() => {
    if (configListRef.value) {
      configListRef.value.scrollTo({
        top: configListRef.value.scrollHeight,
        behavior: 'smooth',
      })
    }
  })
}

const goToDeviceFunction = () => {
  operate.changeActionType(OperateType.action)
  actionSet.changeSetType(SetType.routine)
  actionSet.changeActionType(ActionType.deviceFunction)
}

const min = () => electronAPI.min()
const max = async () => {
  const maximized = Boolean(await electronAPI.wnd('isMaximized'))
  await electronAPI.wnd(maximized ? 'unmaximize' : 'maximize')
  isWindowMaximized.value = !maximized
}
const out = () => electronAPI.close()
</script>

<style lang="less" scoped>
.right-sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
  flex-shrink: 0;
  padding: 24px 18px 18px;
  background: rgba(255, 255, 255, 0.08); /* 右侧边栏背景 */
  backdrop-filter: blur(25px); /* 毛玻璃效果 */
  -webkit-backdrop-filter: blur(25px);
  overflow: hidden; /* 这里必须是 hidden，防止产生外层滚动条 */
  animation: sidebar-in 0.6s var(--transition-base);
  /* 强制裁剪合成层边缘，消除 Chromium backdrop-filter 亚像素渲染白色伪影 */
  -webkit-mask-image: linear-gradient(#000, #000);
  mask-image: linear-gradient(#000, #000);

  .config-header {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 2px 14px;
    background: transparent;
    border-radius: 0;
    margin: 0 0 6px 0;
    border-bottom: none;
    color: var(--sidebar-text);
    box-shadow: none;
    transition: var(--transition-base);
    position: relative;
    z-index: 1;

    &:hover {
      transform: none;
      box-shadow: none;
    }

    .header-main {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      min-width: 0;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
      flex: 1;

      .header-copy {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
        flex-shrink: 0;
      }

      .recommend-header-btn {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        height: 24px;
        padding: 0 9px 0 7px;
        border: none;
        border-radius: var(--radius-m);
        background: rgba(255, 255, 255, 0.72);
        color: var(--brand-color-pure);
        cursor: pointer;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.05em;
        box-shadow:
          0 2px 8px rgba(0, 0, 0, 0.06),
          inset 0 0 0 1px rgba(var(--color-brand-1-rgb), 0.3);
        transition: var(--transition-base);
        flex-shrink: 0;
        white-space: nowrap;

        svg {
          width: 12px;
          height: 12px;
          flex-shrink: 0;
        }

        &:hover {
          background: rgba(255, 255, 255, 0.96);
          box-shadow:
            0 4px 14px rgba(var(--color-brand-1-rgb), 0.18),
            inset 0 0 0 1.5px rgba(var(--color-brand-1-rgb), 0.5);
          transform: translateY(-1px);
        }

        .recommend-header-dot {
          position: absolute;
          top: 3px;
          right: 3px;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--brand-color-pure);
          border: 1.5px solid rgba(255, 255, 255, 0.9);
          animation: dot-pulse 2.4s ease-in-out infinite;
        }
      }
    }

    .header-main-right {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 4px;
      flex-shrink: 0;
      -webkit-app-region: no-drag;
    }

    .window-controls {
      display: flex;
      align-items: center;
      gap: 4px;

      .window-btn {
        width: 30px;
        height: 30px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: none;
        border-radius: var(--radius-m);
        background: transparent;
        color: var(--sidebar-text-muted);
        cursor: pointer;
        transition: var(--transition-base);

        .svg_asset,
        svg {
          width: 14px;
          height: 14px;
          stroke: currentColor;
          stroke-width: 2;
        }

        &:hover {
          background: var(--header-icon-hover-bg);
          color: var(--shell-text-primary);
        }
      }
    }

    .header-actions {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr) minmax(0, 1fr);
      align-items: center;
      gap: 10px;
      width: 100%;
      padding: 2px 0 0;

      .header-actions-label {
        color: var(--sidebar-text-muted);
        font-size: 9px;
        font-weight: 800;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        white-space: nowrap;

        .tip_bubble {
          opacity: 1;
          visibility: visible;
          transform: translateX(-50%) translateY(0);
        }
      }
    }

    .action-item-wide {
      width: 100%;
    }

    .tip_bubble {
      position: absolute;
      top: calc(100% + 10px);
      left: 50%;
      transform: translateX(-50%) translateY(10px);
      background: rgba(0, 0, 0, 0.85);
      backdrop-filter: blur(10px);
      color: #fff;
      padding: 6px 10px;
      border-radius: var(--radius-s);
      font-size: 11px;
      font-weight: 500;
      white-space: nowrap;
      pointer-events: none;
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
      z-index: 1000;

      &::after {
        content: '';
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 5px solid transparent;
        border-bottom-color: rgba(0, 0, 0, 0.85);
      }
    }

    :deep(.mgx_action_btn) {
      width: 100%;
      min-height: 34px;
      padding: 0 12px;
      min-width: 0;
      justify-content: center;
      gap: 8px;
      background: rgba(255, 255, 255, 0.52);
      border: 1px solid rgba(255, 255, 255, 0.44);
      border-radius: var(--radius-m);
      color: var(--sidebar-text);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);

      &:hover {
        transform: translateY(-2px);
        background: rgba(255, 255, 255, 0.9);
        color: var(--brand-color-pure);
        box-shadow: 0 10px 18px rgba(0, 0, 0, 0.06);
      }

      .icon-wrapper {
        margin: 0;
        flex-shrink: 0;

        .svg_asset,
        svg {
          width: 14px;
          height: 14px;
        }
      }

      span {
        display: inline;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
    }
  }

  .header-icon {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: rgba(var(--color-brand-1-rgb), 0.12);
    flex-shrink: 0;

    .svg_asset,
    svg {
      width: 18px;
      height: 18px;
    }
  }

  .header-title {
    font-size: 11px;
    font-weight: 900;
    letter-spacing: 0.18em;
    color: var(--shell-text-primary);
    text-transform: uppercase;
  }
}

@media (max-width: 1360px) {
  .config-header {
    .header-actions {
      grid-template-columns: 1fr 1fr;

      .header-actions-label {
        grid-column: 1 / -1;
      }
    }
  }
}

.config-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &::-webkit-scrollbar {
    width: 4px;
    display: block !important; /* 强制显示 */
  }

  &::-webkit-scrollbar-track {
    background: transparent !important;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1) !important;
    border-radius: var(--radius-pill);
    transition: var(--transition-base);

    &:hover {
      background: rgba(0, 0, 0, 0.16) !important;
    }
  }
}

.config-section-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 4px 2px;

  .config-section-title {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--sidebar-text-muted);
  }

  .config-section-count {
    font-size: 10px;
    font-weight: 600;
    color: var(--sidebar-text-muted);
    opacity: 0.6;
  }
}

/* 极简内嵌分隔线 — 替代大 label 块，只占 18px 高度 */
.config-section-micro-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
  height: 18px;
  gap: 6px;

  span:first-child {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--sidebar-text-muted);
    opacity: 0.55;
    white-space: nowrap;
    flex-shrink: 0;
  }

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
  }

  .micro-count {
    font-size: 9px;
    font-weight: 600;
    color: var(--sidebar-text-muted);
    opacity: 0.4;
    flex-shrink: 0;
  }

  .section-hint-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;

    .section-hint-icon {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      border: 2px solid var(--brand-color-pure, #6c8eff);
      background: var(--bg-card);
      color: var(--brand-color-pure, #6c8eff);
      font-size: 10px;
      font-weight: 900;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: help;
      user-select: none;
      opacity: 1;
      box-shadow:
        0 2px 8px rgba(0, 0, 0, 0.18),
        0 0 0 3px color-mix(in srgb, var(--brand-color-pure, #6c8eff) 22%, transparent);
      animation: hint-icon-pulse 2.8s ease-in-out infinite;
      transition:
        transform 0.15s ease,
        box-shadow 0.15s ease;
    }

    &:hover .section-hint-icon {
      animation: none;
      transform: scale(1.2);
      box-shadow:
        0 3px 12px rgba(0, 0, 0, 0.22),
        0 0 0 4px color-mix(in srgb, var(--brand-color-pure, #6c8eff) 35%, transparent);
    }
  }
}

@keyframes hint-icon-pulse {
  0%,
  100% {
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.18),
      0 0 0 3px color-mix(in srgb, var(--brand-color-pure, #6c8eff) 22%, transparent);
  }
  50% {
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.18),
      0 0 0 5px color-mix(in srgb, var(--brand-color-pure, #6c8eff) 35%, transparent);
  }
}

.config-section-micro-archived {
  margin-top: 4px;
}

.config-section-archive {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.config-item-archived {
  opacity: 0.75;
  background: rgba(255, 255, 255, 0.3);

  &:hover {
    opacity: 1;
  }
}

.config-footer-fixed {
  padding: 4px 2px 20px;
  background: transparent;
  flex-shrink: 0;
}

.jiangguowang-reopen-bar {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 10px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: var(--radius-m);
  cursor: pointer;
  color: rgba(255, 255, 255, 0.65);
  flex-shrink: 0;
  transition: all 0.3s var(--transition-base);

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
    transform: translateY(-1px);
  }

  .reopen-dot {
    width: 5px;
    height: 5px;
    background: var(--brand-color-pure);
    border-radius: 50%;
    box-shadow: 0 0 6px var(--brand-color-pure);
    flex-shrink: 0;
  }

  .reopen-label {
    flex: 1;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
  }

  svg {
    flex-shrink: 0;
  }
}

.add-config {
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.3);
  border: 1px dashed rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  color: var(--sidebar-text-muted);
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  flex-shrink: 0;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);

  .plus-icon {
    width: 16px;
    height: 16px;
    transition: transform 0.4s var(--transition-base);

    .svg_asset {
      width: 100%;
      height: 100%;
    }
  }

  span {
    font-size: 13px;
    font-weight: 700;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.72);
    border-color: rgba(var(--color-brand-1-rgb), 0.24);
    color: var(--brand-color-pure);
    transform: none;
    box-shadow: none;

    .plus-icon {
      transform: rotate(90deg) scale(1.2);
    }
  }
}

/* 动画定义 */
@keyframes sidebar-in {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (max-width: 1560px) {
  .right-sidebar {
    padding: 14px;

    .config-header {
      padding: 0 2px 12px;
    }
  }
}

/* 列表过渡动画 */
.list-complete-enter-from,
.list-complete-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.list-complete-leave-active {
  position: absolute;
  width: calc(100% - 36px);
}
</style>

<!-- Section Hint 全局气泡：Teleport 到 body，不受 overflow 裁剪 -->
<style>
.section-hint-teleport-bubble {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  background: rgba(18, 18, 26, 0.96);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px 13px 11px;
  box-shadow:
    0 8px 28px rgba(0, 0, 0, 0.45),
    0 0 0 0.5px rgba(255, 255, 255, 0.04) inset;
}

.section-hint-teleport-bubble .hint-bubble-title {
  font-size: 10.5px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.55);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 5px;
}

.section-hint-teleport-bubble .hint-bubble-body {
  font-size: 11.5px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.6;
  white-space: normal;
}

/* Transition */
.hint-fade-enter-active,
.hint-fade-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s cubic-bezier(0.23, 1, 0.32, 1);
}
.hint-fade-enter-from,
.hint-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
